import _ from 'lodash';
import i18n from 'i18n';

export function createSelectOptions(
  arrayValues: (string | number)[],
  i18nPrefix = '',
  formatter?: (value: string | number) => string,
  paramObject?: object
): SelectOptions[] {
  return arrayValues.map((value: string | number) => {
    const i18nParam = formatter ? formatter(value) : value;
    const i18nKey = `${i18nPrefix}${i18nParam}`;
    return {
      label: i18n.exists(i18nKey)
        ? String(i18n.t(`${i18nPrefix}${i18nParam}`, paramObject as any))
        : value.toString(),
      value
    };
  });
}

export function pickValues(
  selectOptions: SelectOptions[]
): (string | number)[] {
  return _.map(selectOptions, 'value');
}

export function pickValue(selectOptions: SelectOptions): string | number {
  return _.get(selectOptions, 'value');
}

export function findValue(
  selectOptions: SelectOptions[],
  value: string | number
): SelectOptions | undefined {
  return _.find(selectOptions, { value });
}

export function filter(
  selectOptions: SelectOptions[],
  toBeKeeped: string[] | number[]
) {
  return selectOptions.filter((option) => {
    return toBeKeeped.includes(option.value as never);
  });
}

export function createSelectOptionsFromEnum(
  enumData,
  i18nPrefix = '',
  ignoreValues: any = [],
  valueDecorater = (value) => value.toLowerCase()
): SelectOptions[] {
  return Object.keys(enumData)
    .filter(isNaN as any)
    .reduce((acc: SelectOptions[], value: string) => {
      if (ignoreValues.includes(enumData[value])) {
        return acc;
      }
      acc.push({
        label: i18n.t(`${i18nPrefix}${valueDecorater(value)}`),
        value: enumData[value]
      });
      return acc;
    }, []);
}

/**
 * Build a value → label lookup from a freshly-fetched options bag. Walks
 * nested `options` so child values (e.g. "台北市/996110") are indexed
 * alongside parent values (e.g. "台北市") in one flat map.
 */
export function buildValueLabelMap(
  fresh: SelectOptions[]
): Map<string | number, string> {
  const map = new Map<string | number, string>();
  const walk = (opts: SelectOptions[]) => {
    for (const opt of opts) {
      map.set(opt.value, opt.label);
      if (Array.isArray(opt.options)) walk(opt.options);
    }
  };
  walk(fresh);
  return map;
}

/**
 * Overlay fresh labels onto stored option entries by value lookup. Values are
 * preserved (they're the identity); labels are replaced recursively. When a
 * value is missing from the map (data drift / regrouped to "Others" in one
 * locale but not the other), fall back to the value itself as label so we
 * never display a stale string from a previous locale.
 */
export function overlayLabels(
  entries: SelectOptions[],
  labelMap: Map<string | number, string>
): SelectOptions[] {
  return entries.map((e) => ({
    ...e,
    label: labelMap.get(e.value) ?? String(e.value),
    options: Array.isArray(e.options)
      ? overlayLabels(e.options, labelMap)
      : e.options
  }));
}

export function textToSelectOptions(text: string): SelectOptions[] {
  const labelValueItems = text.replace(/\n$/, '').split('\n');
  return labelValueItems
    .filter((labelValueItem) => labelValueItem.split(',').length >= 2)
    .map((labelValueItem) => {
      const [value, ...labels] = labelValueItem.split(',');
      return {
        label: labels.join(',').trim(),
        value: value.trim()
      };
    });
}
